import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { login as loginApi } from '../api/sysAuth';
import { isSysApiSuccess } from '../api/sysApiCodes';
import {
  clearSysTokens,
  getSysToken,
  getSysUser,
  setSysTokens,
  setSysUser
} from '../api/sysAuthStorage';
import type { LoginCmd, LoginRes, SysUserInfo } from '../api/types/sysAuth';

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(getSysToken());
  const user = ref<SysUserInfo | null>(null);

  const isLoggedIn = computed(() => Boolean(token.value));

  function hydrateFromStorage() {
    token.value = getSysToken();
    if (!user.value) {
      user.value = getSysUser();
    }
  }

  function hasModule(code: string): boolean {
    return user.value?.modules?.some((m) => m.code === code) ?? false;
  }

  function logout() {
    token.value = null;
    user.value = null;
    clearSysTokens();
  }

  async function login(payload: LoginCmd) {
    try {
      const res = await loginApi(payload);
      if (!isSysApiSuccess(res.code) || !res.data) {
        throw new Error(res.msg || '登录失败');
      }
      const { token: t, refreshToken, sysUserInfo } = res.data;
      setSysTokens(t, refreshToken);
      setSysUser(sysUserInfo);
      token.value = t;
      user.value = sysUserInfo;
      return res.data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const body = e.response?.data as { msg?: string } | undefined;
        throw new Error(body?.msg ?? '网络异常，请稍后重试');
      }
      throw e;
    }
  }

  function setUser(u: SysUserInfo | null) {
    user.value = u;
    if (u) {
      setSysUser(u);
    }
  }

  /** 刷新 access token 成功后同步 Pinia 与 localStorage（由 sysHttp 动态调用） */
  function applyRefreshedSession(data: LoginRes) {
    setSysTokens(data.token, data.refreshToken);
    setSysUser(data.sysUserInfo);
    token.value = data.token;
    user.value = data.sysUserInfo;
  }

  return {
    token,
    user,
    isLoggedIn,
    hydrateFromStorage,
    hasModule,
    logout,
    login,
    setUser,
    applyRefreshedSession
  };
});
